import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import { DEFAULT_TRANSCRIPTION_SETTINGS } from "../components/TranscriptionSettings";
import { TranslationPanel } from "../components/TranslationPanel";
import { ApiError, type AsrWord, type DocumentBlock, type ResultDocument, type TranscriptionSpeaker, type WaveformTile } from "../lib/api";
import { localizedError } from "../lib/localized-error";
import { useTasks } from "../tasks";

export interface Cue { id: string; start: number; end: number; text: string; speaker: string }
export interface ResegmentOptions {
  maximumCharactersPerLine: number;
  maximumLines: number;
  maximumDurationMs: number;
  maximumGapMs: number;
}
type EditableDocumentKind = "transcript" | "subtitle";
type EditorTab = "transcript" | "subtitles" | "translation";

const initialCues: Cue[] = [];
const defaultResegmentOptions: ResegmentOptions = {
  maximumCharactersPerLine: 42,
  maximumLines: 2,
  maximumDurationMs: 6_000,
  maximumGapMs: 600,
};

export function EditorPage() {
  const { t, i18n } = useTranslation();
  const { id = "draft" } = useParams();
  const { api, openAuth, session } = useAuth();
  const { prepend, tasks } = useTasks();
  const task = tasks.find((item) => item.id === id);
  const [tab, setTab] = useState<EditorTab>("transcript");
  const [activeKind, setActiveKind] = useState<EditableDocumentKind>("transcript");
  const [documents, setDocuments] = useState<ResultDocument[]>([]);
  const [switchingDocument, setSwitchingDocument] = useState(false);
  const [cues, setCues] = useState<Cue[]>(() => loadCues(id));
  const [history, setHistory] = useState<Cue[][]>([]);
  const [future, setFuture] = useState<Cue[][]>([]);
  const [selectedId, setSelectedId] = useState(cues[0]?.id ?? "");
  const [rangeAnchorId, setRangeAnchorId] = useState(cues[0]?.id ?? "");
  const [rangeEndId, setRangeEndId] = useState(cues[0]?.id ?? "");
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "failed">("saved");
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [replacePending, setReplacePending] = useState(false);
  const [shiftMs, setShiftMs] = useState(0);
  const [pendingShiftScope, setPendingShiftScope] = useState<"all" | "selected" | "start" | null>(null);
  const [startTimecode, setStartTimecode] = useState("00:00:00.000");
  const [resegmentOptions, setResegmentOptions] = useState<ResegmentOptions>(defaultResegmentOptions);
  const [skipSilence, setSkipSilence] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaVersions, setMediaVersions] = useState<{ original: string | null; enhanced: string | null }>({ original: null, enhanced: null });
  const [mediaVersion, setMediaVersion] = useState<"original" | "enhanced">("original");
  const [retranscribePending, setRetranscribePending] = useState(false);
  const [retranscribeBusy, setRetranscribeBusy] = useState(false);
  const [mediaActionMessage, setMediaActionMessage] = useState<string | null>(null);
  const [waveformTiles, setWaveformTiles] = useState<Array<WaveformTile & { object_url: string }>>([]);
  const [timelineWindow, setTimelineWindow] = useState(60_000);
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [speakers, setSpeakers] = useState<TranscriptionSpeaker[]>(() => inferredSpeakers(cues, (number) => t("editor.speaker", { number })));
  const [mergeSpeakerTarget, setMergeSpeakerTarget] = useState("");
  const [words, setWords] = useState<AsrWord[]>([]);
  const saveTimer = useRef<number | null>(null);
  const saveInFlight = useRef<Promise<boolean> | null>(null);
  const dirty = useRef(false);
  const editVersion = useRef(0);
  const baseRevision = useRef<string | null>(null);
  const loadedDocument = useRef(false);
  const skipNextSave = useRef(false);
  const audio = useRef<HTMLAudioElement>(null);
  const playRangeEnd = useRef<number | null>(null);
  const mediaRefreshAttempted = useRef(false);
  const timelineDrag = useRef<{ id: string; mode: "move" | "start" | "end"; startX: number; original: Cue; snapshot: Cue[] } | null>(null);
  const totalDuration = task?.duration_ms ?? Math.max(20_000, ...cues.map((cue) => cue.end));
  const maximumTimelineOffset = Math.max(0, totalDuration - timelineWindow);
  const timelineStart = Math.min(timelineOffset, maximumTimelineOffset);
  const timelineEnd = Math.min(totalDuration, timelineStart + timelineWindow);

  useEffect(() => {
    if (task?.status !== "completed") return;
    let active = true;
    void Promise.all([
      api.getTranscriptionResult(id),
      api.mediaPlaybackUrl(task.source_asset_id).catch(() => null),
      task.audio_repair_mode && task.audio_repair_mode !== "off"
        ? api.mediaPlaybackUrl(`${id}:normalized`).catch(() => null)
        : Promise.resolve(null),
    ]).then(([result, sourceUrl, enhancedUrl]) => {
      if (!active) return;
      setMediaUrl(sourceUrl);
      setMediaVersions({ original: sourceUrl, enhanced: enhancedUrl });
      setMediaVersion("original");
      setWords(result.asr.words);
      setDocuments(result.documents);
      const initial = result.documents.find((document) => document.kind === "transcript") ?? result.documents[0];
      if (!initial) return;
      const loaded = cuesFromBlocks(initial.blocks, initial.id);
      loadDocumentState(initial.id, initial.kind, initial.current_revision_id, loaded);
      setSpeakers(result.speakers.length ? result.speakers : inferredSpeakers(loaded, (number) => t("editor.speaker", { number })));
    }).catch(() => setSaveState("failed"));
    return () => {
      active = false;
    };
  }, [api, id, task?.audio_repair_mode, task?.source_asset_id, task?.status]);

  useEffect(() => {
    if (task?.status !== "completed" || id === "draft") return;
    let active = true;
    let loaded: Array<WaveformTile & { object_url: string }> = [];
    void api.waveformTiles(id, timelineStart, Math.max(timelineStart + 1, timelineEnd)).then((tiles) => {
      loaded = tiles;
      if (!active) {
        tiles.forEach((tile) => URL.revokeObjectURL(tile.object_url));
        return;
      }
      setWaveformTiles((current) => {
        current.forEach((tile) => URL.revokeObjectURL(tile.object_url));
        return tiles;
      });
    }).catch(() => {
      if (active) setWaveformTiles([]);
    });
    return () => {
      active = false;
      loaded.forEach((tile) => URL.revokeObjectURL(tile.object_url));
    };
  }, [api, id, task?.status, timelineEnd, timelineStart]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select, [contenteditable='true']")) return;
      if (event.code === "Space") { event.preventDefault(); togglePlayback(); }
      else if (event.key === "ArrowLeft") { event.preventDefault(); seek(time - 5000); }
      else if (event.key === "ArrowRight") { event.preventDefault(); seek(time + 5000); }
      else if (event.key === "ArrowUp") { event.preventDefault(); selectAdjacentCue(-1); }
      else if (event.key === "ArrowDown") { event.preventDefault(); selectAdjacentCue(1); }
      else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") { event.preventDefault(); event.shiftKey ? redo() : undo(); }
      else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") { event.preventDefault(); redo(); }
      else if (event.key.toLowerCase() === "s") split();
      else if (event.key.toLowerCase() === "m") merge();
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  });

  useEffect(() => {
    if (!playing || mediaUrl) return;
    const timer = window.setInterval(() => setTime((value) => value + 100 * speed), 100);
    return () => window.clearInterval(timer);
  }, [mediaUrl, playing, speed]);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed]);

  useEffect(() => {
    setTimelineOffset((current) => {
      const clamped = Math.min(current, maximumTimelineOffset);
      if (time >= clamped && time < clamped + timelineWindow) return clamped;
      return Math.min(Math.floor(time / timelineWindow) * timelineWindow, maximumTimelineOffset);
    });
  }, [maximumTimelineOffset, time, timelineWindow]);

  useEffect(() => {
    if (audio.current) audio.current.currentTime = time / 1000;
  }, [mediaUrl]);

  useEffect(() => {
    if (!loadedDocument.current || !documentId || !baseRevision.current) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    dirty.current = true;
    editVersion.current += 1;
    setSaveState("saving");
    saveTimer.current = window.setTimeout(() => {
      void persistCurrentDocument();
    }, 500);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [cues, documentId, activeKind]);

  const selected = cues.find((cue) => cue.id === selectedId) ?? cues[0];
  const mergeSpeakerTargets = speakers.filter((speaker) => speaker.speaker_key !== selected?.speaker);
  const resolvedMergeSpeakerTarget = mergeSpeakerTargets.some((speaker) => speaker.speaker_key === mergeSpeakerTarget)
    ? mergeSpeakerTarget
    : mergeSpeakerTargets[0]?.speaker_key ?? "";
  const selectedRangeIds = useMemo(
    () => cueRangeIds(cues, rangeAnchorId || selected?.id || "", rangeEndId || selected?.id || ""),
    [cues, rangeAnchorId, rangeEndId, selected?.id],
  );
  const issues = useMemo(() => qualityIssues(cues), [cues]);
  const replaceCount = useMemo(() => countOccurrences(cues, find), [cues, find]);
  const shiftedPreview = useMemo(() => {
    if (!pendingShiftScope) return null;
    const delta = pendingShiftScope === "start" ? parseTimecode(startTimecode) - (cues[0]?.start ?? 0) : shiftMs;
    return shiftCues(cues, delta, pendingShiftScope === "selected" ? "selected" : "all", selectedRangeIds);
  }, [cues, pendingShiftScope, selectedRangeIds, shiftMs, startTimecode]);

  function loadDocumentState(id: string, kind: EditableDocumentKind, revisionId: string, loaded: Cue[]) {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    skipNextSave.current = true;
    dirty.current = false;
    setCues(loaded);
    setSelectedId(loaded[0]?.id ?? "");
    setRangeAnchorId(loaded[0]?.id ?? "");
    setRangeEndId(loaded[0]?.id ?? "");
    setDocumentId(id);
    setActiveKind(kind);
    baseRevision.current = revisionId;
    loadedDocument.current = true;
    setHistory([]);
    setFuture([]);
    setSaveState("saved");
    setConflict(false);
  }

  async function persistCurrentDocument(): Promise<boolean> {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (saveInFlight.current) {
      const saved = await saveInFlight.current;
      if (!saved) return false;
    }
    if (!dirty.current || !documentId || !baseRevision.current) return true;
    const version = editVersion.current;
    const savingDocumentId = documentId;
    const savingKind = activeKind;
    setSaveState("saving");
    const operation = api.saveDocument(
      savingDocumentId,
      baseRevision.current,
      documentBlocks(cues, savingKind),
    ).then((result) => {
      baseRevision.current = result.revision.id;
      setDocuments((current) => current.map((document) => document.id === savingDocumentId ? {
        ...document,
        current_revision_id: result.revision.id,
        revision_number: result.revision.revision_number,
        blocks: result.blocks,
      } : document));
      if (editVersion.current === version) {
        dirty.current = false;
        setSaveState("saved");
      }
      setConflict(false);
      return true;
    }).catch((reason) => {
      setSaveState("failed");
      if (reason instanceof ApiError && reason.code === "revision_conflict") setConflict(true);
      return false;
    });
    saveInFlight.current = operation;
    const saved = await operation;
    if (saveInFlight.current === operation) saveInFlight.current = null;
    return saved;
  }

  async function switchTab(next: EditorTab) {
    if (next === tab || switchingDocument) return;
    setSwitchingDocument(true);
    try {
      if (!await persistCurrentDocument()) return;
      if (next === "translation") {
        setTab(next);
        return;
      }
      const nextKind: EditableDocumentKind = next === "subtitles" ? "subtitle" : "transcript";
      if (nextKind !== activeKind) {
        const target = documents.find((document) => document.kind === nextKind);
        if (!target) return;
        const latest = await api.getDocument(target.id);
        loadDocumentState(target.id, nextKind, latest.revision.id, cuesFromBlocks(latest.blocks, target.id));
      }
      setTab(next);
    } catch {
      setSaveState("failed");
    } finally {
      setSwitchingDocument(false);
    }
  }

  function commit(next: Cue[]) { setHistory((value) => [...value.slice(-49), cues]); setFuture([]); setCues(next); }
  function selectCue(cueId: string, extendRange = false) {
    setSelectedId(cueId);
    if (!extendRange || !rangeAnchorId || !cues.some((cue) => cue.id === rangeAnchorId)) {
      setRangeAnchorId(cueId);
    }
    setRangeEndId(cueId);
  }
  function updateCue(id: string, patch: Partial<Cue>) { commit(cues.map((cue) => cue.id === id ? { ...cue, ...patch } : cue)); }
  function undo() { const previous = history.at(-1); if (!previous) return; setFuture((value) => [cues, ...value]); setCues(previous); setHistory((value) => value.slice(0, -1)); }
  function redo() { const next = future[0]; if (!next) return; setHistory((value) => [...value, cues]); setCues(next); setFuture((value) => value.slice(1)); }
  function split() {
    if (!selected || selected.text.length < 2) return;
    const midpoint = time > selected.start && time < selected.end ? Math.round(time) : Math.floor((selected.start + selected.end) / 2);
    const ratio = (midpoint - selected.start) / Math.max(1, selected.end - selected.start);
    const target = Math.max(1, Math.min(selected.text.length - 1, Math.round(selected.text.length * ratio)));
    const textMidpoint = nearestBreak(selected.text, target);
    const index = cues.indexOf(selected);
    const next = [...cues];
    next.splice(index, 1,
      { ...selected, end: midpoint, text: selected.text.slice(0, textMidpoint).trimEnd() },
      { ...selected, id: crypto.randomUUID(), start: midpoint, text: selected.text.slice(textMidpoint).trimStart() },
    );
    commit(next);
  }
  function merge() { if (!selected) return; const index = cues.indexOf(selected); const following = cues[index + 1]; if (!following) return; commit([...cues.slice(0, index), { ...selected, end: following.end, text: `${selected.text} ${following.text}` }, ...cues.slice(index + 2)]); }
  function mergePrevious() {
    if (!selected) return;
    const index = cues.indexOf(selected);
    const previous = cues[index - 1];
    if (!previous) return;
    const merged = { ...previous, end: selected.end, text: `${previous.text} ${selected.text}`.trim() };
    commit([...cues.slice(0, index - 1), merged, ...cues.slice(index + 1)]);
    selectCue(merged.id);
  }
  function requestReplaceAll() { if (replaceCount > 0) setReplacePending(true); }
  function replaceAll() {
    if (!find || replaceCount === 0) return;
    commit(cues.map((cue) => ({ ...cue, text: cue.text.replaceAll(find, replace) })));
    setReplacePending(false);
  }
  async function renameSpeaker(speaker: TranscriptionSpeaker, displayName: string) {
    const name = displayName.trim();
    if (!name || name === speaker.display_name) return;
    setSpeakers((current) => current.map((item) => item.id === speaker.id ? { ...item, display_name: name, status: "confirmed" } : item));
    await api.renameSpeaker(id, speaker.id, name).catch(() => setSaveState("failed"));
  }
  async function splitSpeaker() {
    if (!selected || id === "draft") return;
    const created = await api.createSpeaker(id, t("editor.newSpeaker", { number: speakers.length + 1 })).catch(() => null);
    if (!created) { setSaveState("failed"); return; }
    setSpeakers((current) => [...current, created]);
    updateCue(selected.id, { speaker: created.speaker_key });
  }
  function mergeSpeaker(sourceKey: string, targetKey: string) {
    if (!targetKey || sourceKey === targetKey) return;
    commit(cues.map((cue) => cue.speaker === sourceKey ? { ...cue, speaker: targetKey } : cue));
  }
  function requestShift(scope: "all" | "selected") { if (shiftMs) setPendingShiftScope(scope); }
  function requestStartTimecode() {
    if (cues.length && Number.isFinite(parseTimecode(startTimecode))) setPendingShiftScope("start");
  }
  function applyShift() {
    if (!shiftedPreview) return;
    commit(shiftedPreview);
    setPendingShiftScope(null);
  }
  function resegment() {
    const next = resegmentCues(cues, resegmentOptions);
    commit(next);
    selectCue(next[0]?.id ?? "");
  }
  function beginTimelineDrag(event: ReactPointerEvent<HTMLElement>, cue: Cue, mode: "move" | "start" | "end") {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.closest("button")?.setPointerCapture(event.pointerId);
    selectCue(cue.id);
    timelineDrag.current = { id: cue.id, mode, startX: event.clientX, original: cue, snapshot: cues };
  }
  function moveTimelineDrag(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = timelineDrag.current;
    if (!drag || drag.id !== event.currentTarget.dataset.cueId) return;
    const track = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!track?.width) return;
    const delta = snap((event.clientX - drag.startX) / track.width * (timelineEnd - timelineStart), 50);
    const boundaries = drag.snapshot.flatMap((cue) => cue.id === drag.id ? [] : [cue.start, cue.end]);
    setCues((current) => current.map((cue) => {
      if (cue.id !== drag.id) return cue;
      if (drag.mode === "start") return { ...cue, start: snapToBoundary(Math.max(0, Math.min(drag.original.end - 100, drag.original.start + delta)), boundaries) };
      if (drag.mode === "end") return { ...cue, end: snapToBoundary(Math.max(drag.original.start + 100, drag.original.end + delta), boundaries) };
      const duration = drag.original.end - drag.original.start;
      const start = snapToBoundary(Math.max(0, drag.original.start + delta), boundaries);
      return { ...cue, start, end: start + duration };
    }));
  }
  function endTimelineDrag(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = timelineDrag.current;
    if (!drag) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setHistory((value) => [...value.slice(-49), drag.snapshot]);
    setFuture([]);
    timelineDrag.current = null;
  }
  function togglePlayback() {
    if (!audio.current) { setPlaying((value) => !value); return; }
    if (audio.current.paused) void audio.current.play(); else audio.current.pause();
  }
  function selectAdjacentCue(direction: -1 | 1) {
    if (!cues.length) return;
    const current = Math.max(0, cues.findIndex((cue) => cue.id === selected?.id));
    const target = cues[Math.max(0, Math.min(cues.length - 1, current + direction))];
    if (!target) return;
    selectCue(target.id);
    seek(target.start);
  }
  function playCurrent() {
    if (!selected) return;
    const start = Math.max(0, selected.start - 1_000);
    playRangeEnd.current = selected.end + 1_000;
    seek(start);
    if (audio.current) void audio.current.play(); else setPlaying(true);
  }
  function handleTimeUpdate(element: HTMLAudioElement) {
    const current = element.currentTime * 1000;
    if (playRangeEnd.current !== null && current >= playRangeEnd.current) {
      playRangeEnd.current = null;
      element.pause();
      setTime(current);
      return;
    }
    if (skipSilence) {
      const next = cues.find((cue) => cue.end >= current);
      if (next && current < next.start - 250) {
        element.currentTime = next.start / 1000;
        setTime(next.start);
        return;
      }
    }
    setTime(current);
  }
  function seek(next: number) {
    const value = Math.max(0, next);
    setTime(value);
    if (audio.current) audio.current.currentTime = value / 1000;
  }
  function chooseMediaVersion(version: "original" | "enhanced") {
    const url = mediaVersions[version];
    if (!url) return;
    audio.current?.pause();
    setMediaVersion(version);
    setMediaUrl(url);
    mediaRefreshAttempted.current = false;
  }
  async function refreshMediaUrl() {
    if (!task || mediaRefreshAttempted.current) return;
    mediaRefreshAttempted.current = true;
    const assetId = mediaVersion === "enhanced" ? `${id}:normalized` : task.source_asset_id;
    const url = await api.mediaPlaybackUrl(assetId).catch(() => null);
    if (!url) return;
    setMediaVersions((current) => ({ ...current, [mediaVersion]: url }));
    setMediaUrl(url);
    window.setTimeout(() => { mediaRefreshAttempted.current = false; }, 1_000);
  }
  async function retranscribeSelectedAudio() {
    if (!task || retranscribeBusy) return;
    const settings = structuredClone(DEFAULT_TRANSCRIPTION_SETTINGS);
    settings.diarizationEnabled = session?.user != null;
    settings.language = task.requested_language;
    settings.mode = task.mode;
    settings.audioRepairMode = mediaVersion === "enhanced" && task.audio_repair_mode !== "off"
      ? task.audio_repair_mode ?? "off"
      : "off";
    setRetranscribeBusy(true);
    setMediaActionMessage(null);
    try {
      prepend(await api.reprocessTranscription(task, settings));
      setRetranscribePending(false);
      setMediaActionMessage(t("editor.retranscribeCreated"));
    } catch (reason) {
      setMediaActionMessage(localizedError(reason, t));
    } finally {
      setRetranscribeBusy(false);
    }
  }
  async function reloadLatest() {
    if (!documentId) return;
    const latest = await api.getDocument(documentId).catch(() => null);
    if (!latest) return;
    loadDocumentState(documentId, latest.document.kind, latest.revision.id, cuesFromBlocks(latest.blocks, documentId));
  }
  async function saveLocalVersion() {
    if (!documentId) return;
    const latest = await api.getDocument(documentId).catch(() => null);
    if (!latest) return;
    const blocks = documentBlocks(cues, activeKind);
    const result = await api.saveDocument(documentId, latest.revision.id, blocks).catch(() => null);
    if (!result) return;
    baseRevision.current = result.revision.id;
    dirty.current = false;
    setDocuments((current) => current.map((document) => document.id === documentId ? {
      ...document,
      current_revision_id: result.revision.id,
      revision_number: result.revision.revision_number,
      blocks: result.blocks,
    } : document));
    setConflict(false);
    setSaveState("saved");
  }
  async function deliver(email: boolean) {
    if (email && !session?.user) { openAuth(); return; }
    if (!documentId || !baseRevision.current) { setDeliveryError(t("editor.noDocument")); return; }
    setDeliveryBusy(true);
    setDeliveryError(null);
    try {
      const result = await api.createExport(documentId, baseRevision.current, activeKind === "subtitle" ? "srt" : "txt", { speakers: true, timecodes: true });
      if (email) await api.emailExport(result.id);
      else await api.downloadExport(result);
    } catch (reason) {
      setDeliveryError(localizedError(reason, t));
    } finally {
      setDeliveryBusy(false);
    }
  }

  return <div className="editor-page">
    <header className="editor-header"><div className="editor-title"><span className="file-glyph">{task?.content_type?.startsWith("video/") ? "MP4" : "WAV"}</span><p><strong>{task?.title ?? t("editor.untitled")}</strong><small className={`save-state save-state--${saveState}`}>{t(`editor.${saveState}`)}</small>{deliveryError && <small className="delivery-error">{deliveryError}</small>}{task && <span className="retention-summary" title={t("editor.retentionHint")}><Icon name="clock" />{t("editor.mediaUntil", { date: retentionDate(task.media_delete_at_ms, i18n.language) })} · {task.result_delete_at_ms === null ? t("editor.resultsUntilDeleted") : t("editor.resultsUntil", { date: retentionDate(task.result_delete_at_ms, i18n.language) })}</span>}</p></div><div className="editor-tabs"><button disabled={switchingDocument} className={tab === "transcript" ? "is-active" : ""} onClick={() => void switchTab("transcript")}>{t("editor.transcript")}</button><button disabled={switchingDocument} className={tab === "subtitles" ? "is-active" : ""} onClick={() => void switchTab("subtitles")}>{t("editor.subtitles")}</button><button disabled={switchingDocument} className={tab === "translation" ? "is-active" : ""} onClick={() => void switchTab("translation")}>{t("translation.tab")}</button></div><div className="editor-actions"><button className="button button--ghost" disabled={deliveryBusy || tab === "translation"} onClick={() => void deliver(true)}><Icon name="mail" />{t("editor.email")}</button><button className="button button--primary" disabled={deliveryBusy || tab === "translation"} onClick={() => void deliver(false)}><Icon name="download" />{deliveryBusy ? t("common.loading") : t("editor.export")}</button></div></header>
    <div className={`editor-workspace${tab === "translation" ? " editor-workspace--translation" : ""}`}>
      <aside className="media-column"><div className="media-preview"><div className="video-grid" />{mediaUrl && <audio ref={audio} src={mediaUrl} preload="metadata" onTimeUpdate={(event) => handleTimeUpdate(event.currentTarget)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onError={() => void refreshMediaUrl()} />}<button onClick={togglePlayback} aria-label={playing ? t("editor.pause") : t("editor.play")}><Icon name={playing ? "pause" : "play"} /></button><span>{selected?.text}</span></div>{mediaVersions.enhanced && <><div className="media-version-toggle"><button className={mediaVersion === "original" ? "is-active" : ""} onClick={() => chooseMediaVersion("original")}>{t("editor.originalAudio")}</button><button className={mediaVersion === "enhanced" ? "is-active" : ""} onClick={() => chooseMediaVersion("enhanced")}>{t("editor.enhancedAudio")}</button></div><button className="media-retranscribe-button" disabled={retranscribeBusy} onClick={() => { setMediaActionMessage(null); setRetranscribePending(true); }}>{t("editor.retranscribeSelected", { version: t(mediaVersion === "enhanced" ? "editor.enhancedAudio" : "editor.originalAudio") })}</button>{retranscribePending && <div className="media-retranscribe-confirm" role="dialog" aria-label={t("editor.retranscribeTitle")}><strong>{t("editor.retranscribeTitle")}</strong><p>{t("editor.retranscribeConfirm", { version: t(mediaVersion === "enhanced" ? "editor.enhancedAudio" : "editor.originalAudio") })}</p><small>{t("editor.retranscribeBilling")}</small><div><button disabled={retranscribeBusy} onClick={() => setRetranscribePending(false)}>{t("common.cancel")}</button><button className="is-primary" disabled={retranscribeBusy} onClick={() => void retranscribeSelectedAudio()}>{retranscribeBusy ? t("common.loading") : t("editor.retranscribeCreate")}</button></div></div>}{mediaActionMessage && <p className="media-action-message" role="status">{mediaActionMessage}</p>}</>}<div className="player-controls"><button onClick={() => seek(time - 5000)} aria-label={t("editor.backward")}><Icon name="back" /></button><button className="main-play" aria-label={playing ? t("editor.pause") : t("editor.play")} onClick={togglePlayback}><Icon name={playing ? "pause" : "play"} /></button><button onClick={() => seek(time + 5000)} aria-label={t("editor.forward")}><Icon name="forward" /></button><strong className="mono">{formatTime(time)}</strong><select value={speed} onChange={(event) => setSpeed(Number(event.target.value))} aria-label={t("editor.speed")}><option value="0.75">.75×</option><option value="1">1×</option><option value="1.25">1.25×</option><option value="1.5">1.5×</option><option value="2">2×</option></select></div><div className="playback-tools"><button onClick={() => selectAdjacentCue(-1)}><Icon name="back" />{t("editor.previousCue")}</button><button onClick={playCurrent}><Icon name="play" />{t("editor.playCurrent")}</button><button onClick={() => selectAdjacentCue(1)}><Icon name="forward" />{t("editor.nextCue")}</button><label><input type="checkbox" checked={skipSilence} onChange={(event) => setSkipSilence(event.target.checked)} />{t("editor.skipSilence")}</label></div>{tab !== "translation" && <section className="quality-panel"><header><strong>{t("editor.quality")}</strong><span>{t("editor.issueCount", { count: issues.length })}</span></header>{issues.slice(0, 4).map((issue) => <button key={`${issue.cue.id}-${issue.type}`} onClick={() => selectCue(issue.cue.id)}><Icon name="warning" /><span>{t(`editor.${issue.type}`)}</span><em>{formatTime(issue.cue.start)}</em></button>)}</section>}</aside>
      {tab === "translation" ? <TranslationPanel api={api} documents={documents} registered={session?.user != null} onAuth={openAuth} onSeek={seek} /> : <>
      <main className="document-column">{conflict && <div className="revision-conflict" role="alert"><Icon name="warning" /><p><strong>{t("editor.conflictTitle")}</strong><span>{t("editor.conflictHint")}</span></p><button onClick={() => void reloadLatest()}>{t("editor.reloadLatest")}</button><button className="is-primary" onClick={() => void saveLocalVersion()}>{t("editor.saveMine")}</button></div>}<div className="document-toolbar"><label><Icon name="search" /><input value={find} onChange={(event) => { setFind(event.target.value); setReplacePending(false); }} placeholder={t("editor.find")} /></label><input value={replace} onChange={(event) => { setReplace(event.target.value); setReplacePending(false); }} placeholder={t("editor.replace")} /><button disabled={replaceCount === 0} onClick={requestReplaceAll}>{t("editor.replaceCount", { count: replaceCount })}</button><span className="spacer" /><button className="mobile-inspector-toggle" onClick={() => setInspectorOpen(true)} title={t("editor.openInspector")}><Icon name="settings" /></button><button disabled={!history.length} onClick={undo} title={t("editor.undo")}><Icon name="undo" /></button><button disabled={!future.length} onClick={redo} title={t("editor.redo")}><Icon name="redo" /></button></div>{replacePending && <div className="editor-confirm"><span>{t("editor.replaceConfirm", { count: replaceCount })}</span><button onClick={() => setReplacePending(false)}>{t("common.cancel")}</button><button className="is-primary" onClick={replaceAll}>{t("common.apply")}</button></div>}
        {cues.length === 0 ? <div className="editor-empty">{t("editor.noDocument")}</div> : tab === "transcript" ? <div className="transcript-document">{groupBySpeaker(cues).map((group, speakerIndex) => { const speakerRow = speakers.find((item) => item.speaker_key === group.speaker); return <section className="speaker-block" key={group.id}><input className="speaker-name" defaultValue={speakerRow?.display_name ?? t("editor.speaker", { number: speakerIndex + 1 })} aria-label={t("editor.renameSpeaker")} onBlur={(event) => { if (speakerRow) void renameSpeaker(speakerRow, event.target.value); }} /><div className="speaker-paragraphs">{group.blocks.map((cue) => <p key={cue.id} className={cue.id === selected?.id ? "is-selected" : ""} onClick={() => selectCue(cue.id)} onBlur={(event) => { const text = event.currentTarget.textContent ?? ""; if (text !== cue.text) updateCue(cue.id, { text }); }} contentEditable suppressContentEditableWarning>{timedText(cue, words, time, find, seek)}</p>)}</div></section>; })}</div> : <div className="subtitle-list"><div className="subtitle-list-head"><span>{t("editor.cueNumber")}</span><span>{t("library.duration")}</span><span>{t("editor.subtitles")}</span></div>{cues.map((cue, index) => <article key={cue.id} aria-selected={selectedRangeIds.includes(cue.id)} className={`subtitle-cue${cue.id === selected?.id ? " is-active" : ""}${selectedRangeIds.includes(cue.id) ? " is-range-selected" : ""}`} onClick={(event) => { selectCue(cue.id, event.shiftKey); seek(cue.start); }}><span>{index + 1}</span><div className="time-inputs"><input aria-label={t("editor.startTime")} value={formatTime(cue.start)} onChange={(event) => updateCue(cue.id, { start: parseTime(event.target.value) })} /><input aria-label={t("editor.endTime")} value={formatTime(cue.end)} onChange={(event) => updateCue(cue.id, { end: parseTime(event.target.value) })} /></div><textarea value={cue.text} onChange={(event) => updateCue(cue.id, { text: event.target.value })} /><button aria-label={t("editor.remove")} onClick={(event) => { event.stopPropagation(); commit(cues.filter((item) => item.id !== cue.id)); }}><Icon name="trash" /></button></article>)}<button className="add-cue" onClick={() => { const cue = { id: crypto.randomUUID(), start: time, end: time + 2500, text: "", speaker: speakers[0]?.speaker_key ?? "speaker-1" }; commit([...cues, cue].sort((a, b) => a.start - b.start)); selectCue(cue.id); }}><Icon name="plus" />{t("editor.addCue")}</button></div>}
      </main>
      <aside className={`inspector-column${inspectorOpen ? " is-open" : ""}`}><header><h3>{t("editor.subtitles")}</h3><button className="mobile-inspector-close" aria-label={t("common.cancel")} onClick={() => setInspectorOpen(false)}><Icon name="close" /></button></header>{selected && <><label><span>{t("editor.startTime")}</span><input value={formatTime(selected.start)} onChange={(event) => updateCue(selected.id, { start: parseTime(event.target.value) })} /></label><label><span>{t("editor.endTime")}</span><input value={formatTime(selected.end)} onChange={(event) => updateCue(selected.id, { end: parseTime(event.target.value) })} /></label><label><span>{t("editor.assignSpeaker")}</span><select value={selected.speaker} onChange={(event) => updateCue(selected.id, { speaker: event.target.value })}>{speakers.map((speaker) => <option key={speaker.id} value={speaker.speaker_key}>{speaker.display_name}</option>)}</select></label><textarea value={selected.text} onChange={(event) => updateCue(selected.id, { text: event.target.value })} /><div className="inspector-actions"><button onClick={split}><Icon name="edit" />{t("editor.split")}</button><button disabled={cues.indexOf(selected) === 0} onClick={mergePrevious}><Icon name="link" />{t("editor.mergePrevious")}</button><button disabled={cues.indexOf(selected) === cues.length - 1} onClick={merge}><Icon name="link" />{t("editor.merge")}</button><button onClick={() => void splitSpeaker()}><Icon name="plus" />{t("editor.splitSpeaker")}</button>{resolvedMergeSpeakerTarget && <div className="speaker-merge-control"><select aria-label={t("editor.mergeSpeakerTarget")} value={resolvedMergeSpeakerTarget} onChange={(event) => setMergeSpeakerTarget(event.target.value)}>{mergeSpeakerTargets.map((speaker) => <option key={speaker.id} value={speaker.speaker_key}>{speaker.display_name}</option>)}</select><button onClick={() => mergeSpeaker(selected.speaker, resolvedMergeSpeakerTarget)}><Icon name="link" />{t("editor.mergeSpeaker")}</button></div>}</div><section className="timing-tools"><strong>{t("editor.timingTools")}</strong><p className="range-selection-hint">{t("editor.rangeSelected", { count: selectedRangeIds.length })} · {t("editor.rangeHint")}</p><label><span>{t("editor.shiftMs")}</span><input type="number" step="50" value={shiftMs} onChange={(event) => { setShiftMs(Number(event.target.value)); setPendingShiftScope(null); }} /></label><div><button onClick={() => requestShift("selected")}>{t("editor.shiftSelected")}</button><button onClick={() => requestShift("all")}>{t("editor.shiftAll")}</button></div><label><span>{t("editor.startTimecode")}</span><input value={startTimecode} pattern="[0-9]{1,2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}" onChange={(event) => { setStartTimecode(event.target.value); setPendingShiftScope(null); }} /></label><button disabled={!cues.length || !Number.isFinite(parseTimecode(startTimecode))} onClick={requestStartTimecode}>{t("editor.setStartTimecode")}</button>{pendingShiftScope && shiftedPreview && <div className="timing-confirm"><span>{t("editor.shiftConfirm", { count: pendingShiftScope === "selected" ? selectedRangeIds.length : cues.length, issues: qualityIssues(shiftedPreview).length })}</span><button onClick={() => setPendingShiftScope(null)}>{t("common.cancel")}</button><button className="is-primary" onClick={applyShift}>{t("common.apply")}</button></div>}<strong className="segmentation-title">{t("editor.segmentationSettings")}</strong><div className="segmentation-grid"><label><span>{t("editor.maxCharactersPerLine")}</span><input type="number" min="8" max="80" value={resegmentOptions.maximumCharactersPerLine} onChange={(event) => setResegmentOptions((current) => ({ ...current, maximumCharactersPerLine: Number(event.target.value) }))} /></label><label><span>{t("editor.maxLines")}</span><input type="number" min="1" max="3" value={resegmentOptions.maximumLines} onChange={(event) => setResegmentOptions((current) => ({ ...current, maximumLines: Number(event.target.value) }))} /></label><label><span>{t("editor.maxDurationMs")}</span><input type="number" min="500" max="30000" step="100" value={resegmentOptions.maximumDurationMs} onChange={(event) => setResegmentOptions((current) => ({ ...current, maximumDurationMs: Number(event.target.value) }))} /></label><label><span>{t("editor.maxGapMs")}</span><input type="number" min="0" max="10000" step="50" value={resegmentOptions.maximumGapMs} onChange={(event) => setResegmentOptions((current) => ({ ...current, maximumGapMs: Number(event.target.value) }))} /></label></div><button onClick={resegment}>{t("editor.resegment")}</button></section></>}</aside>
      <footer className="editor-timeline"><div className="timeline-head"><div className="timeline-ruler">{Array.from({ length: 5 }, (_, index) => timelineStart + index * (timelineEnd - timelineStart) / 4).map((timestamp) => <span key={timestamp}>{formatTime(timestamp)}</span>)}</div><label className="timeline-pan">{t("editor.timelineScroll")}<input type="range" min="0" max={maximumTimelineOffset} step="1000" value={timelineStart} disabled={maximumTimelineOffset === 0} onChange={(event) => setTimelineOffset(Number(event.target.value))} /></label><label>{t("editor.zoom")}<select aria-label={t("editor.timelineWindow")} value={timelineWindow} onChange={(event) => setTimelineWindow(Number(event.target.value))}><option value="30000">{t("editor.seconds", { count: 30 })}</option><option value="60000">{t("editor.minutes", { count: 1 })}</option><option value="300000">{t("editor.minutes", { count: 5 })}</option></select></label></div><div className="waveform" onClick={(event) => { const rect = event.currentTarget.getBoundingClientRect(); seek(timelineStart + ((event.clientX - rect.left) / rect.width) * (timelineEnd - timelineStart)); }}>{waveformTiles.map((tile) => <img key={tile.id} src={tile.object_url} alt="" style={{ left: `${(tile.start_ms - timelineStart) / (timelineEnd - timelineStart) * 100}%`, width: `${(tile.end_ms - tile.start_ms) / (timelineEnd - timelineStart) * 100}%` }} />)}<b className="playhead" style={{ left: `${Math.max(0, Math.min(100, (time - timelineStart) / (timelineEnd - timelineStart) * 100))}%` }} /></div><div className="cue-track">{cues.filter((cue) => cue.end > timelineStart && cue.start < timelineEnd).map((cue) => <button data-cue-id={cue.id} key={cue.id} className={`${cue.id === selected?.id ? "is-active" : ""}${selectedRangeIds.includes(cue.id) ? " is-range-selected" : ""}`.trim()} style={{ left: `${(cue.start - timelineStart) / (timelineEnd - timelineStart) * 100}%`, width: `${Math.max(1, (cue.end - cue.start) / (timelineEnd - timelineStart) * 100)}%` }} onPointerDown={(event) => beginTimelineDrag(event, cue, "move")} onPointerMove={moveTimelineDrag} onPointerUp={endTimelineDrag}><span className="cue-handle cue-handle--start" onPointerDown={(event) => beginTimelineDrag(event, cue, "start")} /><span>{cue.text}</span><span className="cue-handle cue-handle--end" onPointerDown={(event) => beginTimelineDrag(event, cue, "end")} /></button>)}</div></footer>
      </>}
    </div>
  </div>;
}

function loadCues(id: string): Cue[] { try { const value = JSON.parse(localStorage.getItem(`raven-scribe.editor.${id}`) ?? "null") as Cue[] | null; return value?.length ? value : initialCues; } catch { return initialCues; } }
function cuesFromBlocks(blocks: DocumentBlock[], documentId: string): Cue[] {
  return blocks.map((block, index) => ({
    id: block.id ?? `${documentId}-${index}`,
    start: block.start_ms,
    end: block.end_ms,
    text: block.text,
    speaker: block.speaker_key ?? "speaker-1",
  }));
}
function documentBlocks(cues: Cue[], kind: EditableDocumentKind): DocumentBlock[] {
  return cues.map((cue, ordinal) => ({
    ordinal,
    block_kind: kind === "subtitle" ? "cue" : "paragraph",
    start_ms: cue.start,
    end_ms: cue.end,
    text: cue.text,
    speaker_key: cue.speaker,
    metadata: {},
  }));
}
function groupBySpeaker(cues: Cue[]): Array<{ id: string; speaker: string; blocks: Cue[] }> {
  const groups: Array<{ id: string; speaker: string; blocks: Cue[] }> = [];
  for (const cue of cues) {
    const current = groups.at(-1);
    if (current?.speaker === cue.speaker) current.blocks.push(cue);
    else groups.push({ id: `${cue.speaker}:${cue.id}`, speaker: cue.speaker, blocks: [cue] });
  }
  return groups;
}
function formatTime(value: number): string { const safe = Math.max(0, Math.round(value)); const minutes = Math.floor(safe / 60_000); const seconds = Math.floor((safe % 60_000) / 1000); const millis = safe % 1000; return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`; }
function retentionDate(timestamp: number | null, locale: string): string { return timestamp === null ? "—" : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(timestamp); }
function parseTime(value: string): number { const match = /^(\d+):(\d{2})\.(\d{1,3})$/u.exec(value.trim()); return match ? Number(match[1]) * 60_000 + Number(match[2]) * 1000 + Number(match[3].padEnd(3, "0")) : 0; }
function parseTimecode(value: string): number {
  const match = /^(\d{1,2}):(\d{2}):(\d{2})\.(\d{1,3})$/u.exec(value.trim());
  if (!match || Number(match[2]) >= 60 || Number(match[3]) >= 60) return Number.NaN;
  return Number(match[1]) * 3_600_000 + Number(match[2]) * 60_000 + Number(match[3]) * 1_000 + Number(match[4].padEnd(3, "0"));
}
function highlight(text: string, query: string): React.ReactNode { if (!query) return text; const parts = text.split(query); return parts.map((part, index) => <span key={index}>{index > 0 && <mark>{query}</mark>}{part}</span>); }
function timedText(cue: Cue, words: AsrWord[], time: number, query: string, seek: (value: number) => void): React.ReactNode {
  const matching = words.filter((word) => word.end_ms > cue.start && word.start_ms < cue.end);
  if (!matching.length || normalizedText(matching.map((word) => word.text).join("")) !== normalizedText(cue.text)) {
    return <span className={time >= cue.start && time <= cue.end ? "active-word" : ""} onClick={() => seek(cue.start)}>{highlight(cue.text, query)}</span>;
  }
  return matching.map((word) => <span key={word.id} className={time >= word.start_ms && time <= word.end_ms ? "active-word" : ""} onClick={(event) => { event.stopPropagation(); seek(word.start_ms); }}>{highlight(word.text, query)}</span>);
}
function normalizedText(value: string): string { return value.normalize("NFKC").replaceAll(/[\p{P}\p{S}\s]/gu, "").toLocaleLowerCase(); }
export function countOccurrences(cues: Cue[], query: string): number {
  if (!query) return 0;
  return cues.reduce((total, cue) => total + cue.text.split(query).length - 1, 0);
}
type QualityIssue = { cue: Cue; type: "highCps" | "overlap" | "emptyCue" | "invalidTime" | "lineTooLong" | "gapTooShort" };
export function qualityIssues(cues: Cue[]): QualityIssue[] {
  return cues.flatMap((cue, index) => {
    const result: QualityIssue[] = [];
    const cps = cue.text.length / Math.max((cue.end - cue.start) / 1000, 0.1);
    if (!cue.text.trim()) result.push({ cue, type: "emptyCue" });
    if (cue.end <= cue.start) result.push({ cue, type: "invalidTime" });
    if (cps > 20) result.push({ cue, type: "highCps" });
    if (Math.max(...cue.text.split(/\r?\n/u).map((line) => [...line].length), 0) > 42) result.push({ cue, type: "lineTooLong" });
    if (cues[index + 1] && cue.end > cues[index + 1]!.start) result.push({ cue, type: "overlap" });
    else if (cues[index + 1] && cues[index + 1]!.start - cue.end < 80) result.push({ cue, type: "gapTooShort" });
    return result;
  });
}
export function shiftCues(
  cues: Cue[],
  delta: number,
  scope: "all" | "selected",
  selectedIds: string | readonly string[],
): Cue[] {
  const selected = new Set(typeof selectedIds === "string" ? [selectedIds] : selectedIds);
  return cues.map((cue) => {
    if (scope === "selected" && !selected.has(cue.id)) return cue;
    const duration = Math.max(100, cue.end - cue.start);
    const start = Math.max(0, cue.start + delta);
    return { ...cue, start, end: start + duration };
  }).sort((left, right) => left.start - right.start);
}
export function cueRangeIds(cues: Cue[], anchorId: string, endId: string): string[] {
  const anchor = cues.findIndex((cue) => cue.id === anchorId);
  const end = cues.findIndex((cue) => cue.id === endId);
  if (anchor < 0 && end < 0) return [];
  if (anchor < 0) return [cues[end]!.id];
  if (end < 0) return [cues[anchor]!.id];
  return cues.slice(Math.min(anchor, end), Math.max(anchor, end) + 1).map((cue) => cue.id);
}
export function resegmentCues(
  cues: Cue[],
  options: ResegmentOptions,
  createId: () => string = () => crypto.randomUUID(),
): Cue[] {
  const maximumCharactersPerLine = boundedEditorInteger(options.maximumCharactersPerLine, 8, 80, 42);
  const maximumLines = boundedEditorInteger(options.maximumLines, 1, 3, 2);
  const maximumDurationMs = boundedEditorInteger(options.maximumDurationMs, 500, 30_000, 6_000);
  const maximumGapMs = boundedEditorInteger(options.maximumGapMs, 0, 10_000, 600);
  const fragments = cues.flatMap((cue) => cueFragments(
    cue,
    maximumCharactersPerLine,
    maximumLines,
    maximumDurationMs,
  ));
  const output: Cue[] = [];
  const usedSourceIds = new Set<string>();
  let pending: SegmentFragment[] = [];

  const flush = () => {
    if (!pending.length) return;
    const first = pending[0]!;
    const last = pending.at(-1)!;
    const text = wrapSubtitleText(joinFragmentText(pending), maximumCharactersPerLine, maximumLines)
      ?? joinFragmentText(pending);
    const id = usedSourceIds.has(first.sourceId) ? createId() : first.sourceId;
    usedSourceIds.add(first.sourceId);
    output.push({ id, start: first.start, end: last.end, text, speaker: first.speaker });
    pending = [];
  };

  for (const fragment of fragments) {
    const previous = pending.at(-1);
    if (!previous) {
      pending.push(fragment);
      continue;
    }
    const gap = fragment.start - previous.end;
    const candidate = joinFragmentText([...pending, fragment]);
    const crossesSentence = previous.strongEnd;
    const exceedsLayout = wrapSubtitleText(candidate, maximumCharactersPerLine, maximumLines) === null;
    const exceedsDuration = fragment.end - pending[0]!.start > maximumDurationMs;
    if (
      previous.speaker !== fragment.speaker || gap < 0 || gap > maximumGapMs ||
      crossesSentence || exceedsLayout || exceedsDuration
    ) {
      flush();
    }
    pending.push(fragment);
  }
  flush();
  return output;
}
export function nearestBreak(text: string, target: number): number {
  for (let distance = 0; distance < text.length; distance += 1) {
    const after = target + distance;
    if (after < text.length && /\s|[,.!?;:，。！？；：]/u.test(text[after]!)) return after + 1;
    const before = target - distance;
    if (before > 0 && /\s|[,.!?;:，。！？；：]/u.test(text[before]!)) return before + 1;
  }
  return target;
}
function inferredSpeakers(cues: Cue[], label: (number: number) => string): TranscriptionSpeaker[] { return [...new Set(cues.map((cue) => cue.speaker))].map((speakerKey, index) => ({ id: speakerKey, speaker_key: speakerKey, display_name: label(index + 1), color: "#aeb4b8", status: "unconfirmed" })); }
function snap(value: number, interval: number): number { return Math.round(value / interval) * interval; }
function snapToBoundary(value: number, boundaries: number[]): number { const closest = boundaries.reduce((best, boundary) => Math.abs(boundary - value) < Math.abs(best - value) ? boundary : best, value); return Math.abs(closest - value) <= 150 ? closest : value; }
interface SegmentFragment {
  sourceId: string;
  speaker: string;
  start: number;
  end: number;
  text: string;
  strongEnd: boolean;
}
function cueFragments(
  cue: Cue,
  maximumCharactersPerLine: number,
  maximumLines: number,
  maximumDurationMs: number,
): SegmentFragment[] {
  const text = cue.text.replaceAll(/\s+/gu, " ").trim();
  if (!text) return [{ sourceId: cue.id, speaker: cue.speaker, start: cue.start, end: cue.end, text: "", strongEnd: false }];
  const capacity = maximumCharactersPerLine * maximumLines;
  const characters = [...text].length;
  const durationParts = Math.max(1, Math.ceil(Math.max(0, cue.end - cue.start) / maximumDurationMs));
  const pieceLimit = Math.max(1, Math.min(capacity, Math.ceil(characters / durationParts)));
  const semanticParts = text.match(/[^,.!?;:，。！？；：]+(?:[,.!?;:，。！？；：]+|$)/gu) ?? [text];
  const parts = semanticParts
    .flatMap((part) => splitTextAtNaturalBoundaries(part.trim(), pieceLimit))
    .flatMap((part) => splitTextForLayout(part, maximumCharactersPerLine, maximumLines))
    .filter(Boolean);
  const totalCharacters = Math.max(1, parts.reduce((total, part) => total + [...part].length, 0));
  let consumed = 0;
  return parts.map((part, index) => {
    const start = index === 0
      ? cue.start
      : Math.round(cue.start + (cue.end - cue.start) * consumed / totalCharacters);
    consumed += [...part].length;
    const end = index === parts.length - 1
      ? cue.end
      : Math.round(cue.start + (cue.end - cue.start) * consumed / totalCharacters);
    return {
      sourceId: cue.id,
      speaker: cue.speaker,
      start,
      end: Math.max(start, end),
      text: part,
      strongEnd: /[.!?。！？]["'”’）】]*$/u.test(part),
    };
  });
}
function splitTextAtNaturalBoundaries(text: string, maximum: number): string[] {
  const characters = [...text];
  if (characters.length <= maximum) return [text];
  const parts: string[] = [];
  let remaining = characters;
  while (remaining.length > maximum) {
    const window = remaining.slice(0, maximum + 1);
    let boundary = -1;
    for (let index = Math.min(maximum, window.length - 1); index >= Math.floor(maximum * 0.55); index -= 1) {
      if (/\s|[,;:，；：]/u.test(window[index]!)) {
        boundary = /\s/u.test(window[index]!) ? index : index + 1;
        break;
      }
    }
    if (boundary <= 0) {
      for (let index = Math.min(maximum, window.length - 1); index > 0; index -= 1) {
        if (/\s/u.test(window[index]!)) { boundary = index; break; }
      }
    }
    if (boundary <= 0) boundary = maximum;
    parts.push(remaining.slice(0, boundary).join("").trim());
    remaining = remaining.slice(boundary);
    while (/\s/u.test(remaining[0] ?? "")) remaining = remaining.slice(1);
  }
  if (remaining.length) parts.push(remaining.join("").trim());
  return parts.filter(Boolean);
}
function wrapSubtitleText(text: string, maximumPerLine: number, maximumLines: number): string | null {
  const parts = splitTextAtNaturalBoundaries(text.replaceAll(/\s+/gu, " ").trim(), maximumPerLine);
  return parts.length <= maximumLines ? parts.join("\n") : null;
}
function splitTextForLayout(text: string, maximumPerLine: number, maximumLines: number): string[] {
  const lines = splitTextAtNaturalBoundaries(text, maximumPerLine);
  if (lines.length <= maximumLines) return [text];
  const output: string[] = [];
  for (let index = 0; index < lines.length; index += maximumLines) {
    output.push(joinTextParts(lines.slice(index, index + maximumLines)));
  }
  return output;
}
function joinTextParts(parts: string[]): string {
  return parts.reduce((text, part) => {
    if (!text) return part;
    const separator = /[\p{Script=Latin}\p{N}]$/u.test(text) && /^[\p{Script=Latin}\p{N}]/u.test(part) ? " " : "";
    return `${text}${separator}${part}`;
  }, "");
}
function joinFragmentText(fragments: SegmentFragment[]): string {
  return joinTextParts(fragments.map((fragment) => fragment.text));
}
function boundedEditorInteger(value: number, minimum: number, maximum: number, fallback: number): number {
  return Number.isSafeInteger(value) && value >= minimum && value <= maximum ? value : fallback;
}
